import { CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { DcaStrategy } from '@/types/dca';
import { useDcaRequirements } from '@/core/hooks/useDcaRequirements';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import './dca-status-indicator.scss';

interface Props {
  dcaStrategy: Partial<DcaStrategy>;
  variant?: 'full' | 'compact';
}

const DcaStatusIndicator = ({ dcaStrategy, variant = 'full' }: Props) => {
  const { t } = useTranslation();
  const { requirements, completedCount, totalCount, isReadyToActivate, completionPercentage } = useDcaRequirements(dcaStrategy);

  // Get missing requirements for tooltip
  const missingRequirements = requirements
    .filter(r => !r.completed)
    .map(r => `• ${r.message}`)
    .join('\n');

  const getIcon = (completed: boolean) => {
    if (completed) {
      return <CheckCircleIcon size={20} weight="fill" className="status-icon status-icon--success" />;
    }
    return <WarningCircleIcon size={20} weight="fill" className="status-icon status-icon--error" />;
  };

  if (variant === 'compact') {
    return (
      <div className="dca-status-indicator dca-status-indicator--compact">
        <div className="status-summary">
          <div className="status-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
            <span className="progress-text">
              {completedCount}/{totalCount} {t('dca.requirements.completed')}
            </span>
          </div>
          {isReadyToActivate ? (
            <CheckCircleIcon size={24} weight="fill" className="status-icon status-icon--success" />
          ) : (
            <>
              <WarningCircleIcon
                size={24}
                weight="fill"
                className="status-icon status-icon--warning"
                data-tooltip-id="dca-requirements-tooltip"
                style={{ cursor: 'pointer' }}
              />
              <Tooltip
                id="dca-requirements-tooltip"
                place="left"
                content={missingRequirements}
                style={{ whiteSpace: 'pre-line', zIndex: 9999 }}
              />
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="dca-status-indicator">
      <div className="status-header">
        <h3 className="status-title">{t('dca.requirements.title')}</h3>
        <div className="status-badge">
          {isReadyToActivate ? (
            <span className="badge badge--success">
              <CheckCircleIcon size={16} weight="fill" />
              {t('dca.requirements.ready')}
            </span>
          ) : (
            <span className="badge badge--warning">
              <WarningCircleIcon size={16} weight="fill" />
              {completedCount}/{totalCount} {t('dca.requirements.completed')}
            </span>
          )}
        </div>
      </div>

      <div className="status-progress-bar">
        <div
          className="progress-bar-fill"
          style={{ width: `${completionPercentage}%` }}
        />
      </div>

      <div className="status-requirements">
        {requirements.map((req) => (
          <div
            key={req.id}
            className={`requirement-item ${req.completed ? 'requirement-item--completed' : 'requirement-item--pending'}`}
          >
            <div className="requirement-header">
              {getIcon(req.completed)}
              <span className="requirement-label">{req.label}</span>
            </div>
            <p className="requirement-message">{req.message}</p>
          </div>
        ))}
      </div>

      {!isReadyToActivate && (
        <div className="status-footer">
          <WarningCircleIcon size={16} />
          <span>{t('dca.requirements.complete_all')}</span>
        </div>
      )}
    </div>
  );
};

export default DcaStatusIndicator;
